import React from 'react';
import { Workflow, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { ordersApi, type ErpIntegrationSettingsValues } from '@/lib/api';
import { cn } from '@/lib/utils';

export const ErpIntegrationSettings: React.FC = () => {
  const [settings, setSettings] = React.useState<ErpIntegrationSettingsValues>({ directSend: false, shadow: false });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    ordersApi
      .getErpIntegrationSettings()
      .then((result) => {
        if (active) setSettings(result);
      })
      .catch(() => {
        // Sem leitura (rota só de administrador): mostra os padrões, tudo desligado.
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const update = async (change: Partial<ErpIntegrationSettingsValues>, message: string) => {
    setSaving(true);
    try {
      setSettings(await ordersApi.setErpIntegrationSettings(change));
      toast.success(message);
    } catch {
      toast.error('Não foi possível alterar a configuração. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleDirectSend = (checked: boolean) => {
    if (checked) {
      const confirmed = window.confirm(
        'Com "Direto ao ERP" ligado, TODOS os pedidos deste ambiente ainda não enviados passam a ir direto ao ERP ' +
        '(sem lambda nem Magento), para todos os usuários, até você desligar.\n\nDeseja continuar?'
      );
      if (!confirmed) return;
    }
    void update(
      { directSend: checked },
      checked ? 'Pedidos passam a ir direto ao ERP.' : 'Pedidos voltam a ir pelo Magento.'
    );
  };

  const handleShadow = (checked: boolean) =>
    void update({ shadow: checked }, checked ? 'Modo sombra ligado.' : 'Modo sombra desligado.');

  const disabled = loading || saving;

  return (
    <div className="space-y-4">
      <div
        className={cn(
          'flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between',
          settings.directSend ? 'border-sky-300 bg-sky-50/60' : 'border-emerald-200 bg-emerald-50/60'
        )}
      >
        <div className="flex items-start gap-3">
          <div className={cn('rounded-md p-2', settings.directSend ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700')}>
            <Workflow className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Destino dos pedidos (global)</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Padrão: fluxo antigo (lambda e Magento). Com a opção em ERP, os pedidos ainda não enviados deste ambiente seguem
              direto ao ERP, sem lambda nem Magento, para todos os usuários. A configuração fica no banco e vale até alterar.
            </p>
          </div>
        </div>
        <div className="flex min-w-fit items-center gap-3 self-end sm:self-auto">
          <span className={cn('text-xs font-bold', !settings.directSend ? 'text-emerald-700' : 'text-muted-foreground')}>
            MAGENTO
          </span>
          <Switch
            checked={settings.directSend}
            disabled={disabled}
            onCheckedChange={handleDirectSend}
            aria-label="Alternar o destino dos pedidos entre Magento (fluxo antigo) e direto ao ERP"
            className="data-[state=checked]:bg-sky-500 data-[state=unchecked]:bg-emerald-500"
          />
          <span className={cn('text-xs font-bold', settings.directSend ? 'text-sky-700' : 'text-muted-foreground')}>
            ERP
          </span>
          <span className={cn('min-w-[148px] rounded-full px-3 py-1 text-center text-xs font-semibold',
            settings.directSend ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700')}>
            {loading ? 'Carregando...' : settings.directSend ? 'Enviando direto ao ERP' : 'Enviando ao Magento'}
          </span>
        </div>
      </div>

      <div
        className={cn(
          'flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between',
          settings.shadow ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-slate-50/60'
        )}
      >
        <div className="flex items-start gap-3">
          <div className={cn('rounded-md p-2', settings.shadow ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600')}>
            <FlaskConical className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Modo sombra (global)</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Os pedidos que seguem o fluxo antigo também têm o payload do ERP montado e gravado no passo a passo, sem enviar
              nada ao ERP. Serve para comparar com o que o Linkapi envia hoje.
            </p>
          </div>
        </div>
        <div className="flex min-w-fit items-center gap-3 self-end sm:self-auto">
          <Switch
            checked={settings.shadow}
            disabled={disabled}
            onCheckedChange={handleShadow}
            aria-label="Ligar ou desligar o modo sombra da integração com o ERP"
            className="data-[state=checked]:bg-amber-500"
          />
          <span className={cn('min-w-[148px] rounded-full px-3 py-1 text-center text-xs font-semibold',
            settings.shadow ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600')}>
            {settings.shadow ? 'Modo sombra ligado' : 'Modo sombra desligado'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ErpIntegrationSettings;
